import { TFunction, i18n } from 'i18next'

/**
 * Gets a nested value from an object using a dot-separated path.
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

/**
 * Creates a wrapped translation function that forces the 'taxes' namespace.
 *
 * This bypasses i18next's key parsing issues when nsSeparator and keySeparator
 * are both set to '.', by directly accessing the resource bundle.
 *
 * @param _t - The translation function from useTranslation (unused, kept for API compatibility)
 * @param i18nInstance - The i18n instance from useTranslation hook
 * @returns A wrapped translation function that always uses the 'taxes' namespace
 */
export function createTaxesT(_t: TFunction, i18nInstance?: i18n): TFunction {
  return ((key: string, options?: any) => {
    // Defensive check: ensure i18nInstance exists and has getResourceBundle
    if (!i18nInstance || typeof i18nInstance.getResourceBundle !== 'function') {
      // Fallback: return last segment of key in title case
      const fallback = key.split('.').pop() || key
      return fallback
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim()
    }

    const language = i18nInstance.language || 'en'
    const bundle = i18nInstance.getResourceBundle(language, 'taxes')

    if (!bundle) {
      // Fallback: return last segment of key
      return key.split('.').pop() || key
    }

    // Directly access the nested value, bypassing i18next's key parsing
    let value = getNestedValue(bundle, key)

    // Handle interpolation if options provided
    if (typeof value === 'string' && options) {
      Object.entries(options).forEach(([optKey, optVal]) => {
        if (optKey !== 'ns' && optKey !== 'defaultValue') {
          value = value.replace(
            new RegExp(`{{${optKey}}}`, 'g'),
            String(optVal)
          )
        }
      })
    }

    return value ?? options?.defaultValue ?? key.split('.').pop() ?? key
  }) as TFunction
}
